import { useEffect, useLayoutEffect } from "react";
import { BrowserRouter, Link, Route, Routes, useParams } from "react-router-dom";
import { sciezkaLinku } from "@shared/recenzjeTypy.js";
import { NiewaznyLink } from "./components/recenzja/NiewaznyLink.js";
import { PowlokaRecenzji } from "./components/recenzja/PowlokaRecenzji.js";
import { Card } from "./components/ui/Card.js";
import { StanPusty } from "./components/ui/stany.js";
import { ListaRecenzjiPage } from "./pages/recenzja/ListaRecenzjiPage.js";
import { RozmowaRecenzjiPage } from "./pages/recenzja/RozmowaRecenzjiPage.js";
import { UkladRecenzji } from "./pages/recenzja/UkladRecenzji.js";

/**
 * Aplikacja recenzenta: wszystko pod /r/<token>. NIE MA bramki logowania ani /api/auth/me: jedyna "autoryzacja" to token
 * w adresie, a odpowiedz 404 `link_nieprawidlowy` pokazuje przyjazna strone (UkladRecenzji). Wybor miedzy ta aplikacja
 * a App stoi w main.tsx (po adresie), nie w App - tam bylby to wczesny return przed hookami.
 *
 * Providery (motyw, toasty, dymki) dostarcza main.tsx, te same co dla App.
 */

/** Meta noindex i brak Referera: adres z tokenem nie ma trafiac do wyszukiwarek ani do naglowkow cudzych serwerow. */
function useMetaPrywatnosci(): void {
  useEffect(() => {
    const dodane: HTMLMetaElement[] = [];
    for (const [nazwa, tresc] of [
      ["robots", "noindex, nofollow"],
      ["referrer", "no-referrer"],
    ] as const) {
      const meta = document.createElement("meta");
      meta.name = nazwa;
      meta.content = tresc;
      document.head.appendChild(meta);
      dodane.push(meta);
    }
    return () => {
      for (const meta of dodane) meta.remove();
    };
  }, []);

  // Domyslny tytul karty. useLayoutEffect, bo dziala PRZED efektami stron pod spodem: te ustawiaja wlasne tytuly
  // (useTytulStrony) i nie moga zostac nadpisane domyslnym przy pierwszym montowaniu.
  useLayoutEffect(() => {
    const staryTytul = document.title;
    document.title = "Recenzja - ConvoLens";
    return () => {
      document.title = staryTytul;
    };
  }, []);
}

/** S-8: nieznany adres pod dzialajacym linkiem dostaje zaprojektowany stan, a nie pusta powloke. */
function NieMaStrony(): React.JSX.Element {
  const { token = "" } = useParams();
  return (
    <>
      <h1 className="page-title">Nie ma takiej strony</h1>
      <Card>
        <StanPusty
          tytul="Ta strona nie istnieje"
          opis="Sprawdź adres albo wróć do listy rozmów."
          akcja={
            <Link className="btn btn--secondary" to={sciezkaLinku(token)}>
              Wszystkie rozmowy
            </Link>
          }
        />
      </Card>
    </>
  );
}

/** Trasy bez routera: komponent wchodzi do MemoryRouter w testach i do BrowserRouter w aplikacji. */
export function TrasyRecenzenta(): React.JSX.Element {
  return (
    <Routes>
      <Route path="/r/:token" element={<UkladRecenzji />}>
        <Route index element={<ListaRecenzjiPage />} />
        <Route path="rozmowa/:sessionId" element={<RozmowaRecenzjiPage />} />
        <Route path="*" element={<NieMaStrony />} />
      </Route>
      {/* Adres /r/ bez tokenu albo cokolwiek innego: ta sama przyjazna strona co dla nieważnego linku. */}
      <Route
        path="*"
        element={
          <PowlokaRecenzji etykieta={null}>
            <NiewaznyLink />
          </PowlokaRecenzji>
        }
      />
    </Routes>
  );
}

export function ReviewerApp(): React.JSX.Element {
  useMetaPrywatnosci();
  return (
    <BrowserRouter>
      <TrasyRecenzenta />
    </BrowserRouter>
  );
}
